import React, { useEffect, useState } from "react";
import { Box, Typography, Tab, Tabs } from "@mui/material";
import ReactPlayer from "react-player";
import { useTheme } from "@mui/material/styles";
import { BrandContext } from "../../App";
import { ArrowForwardRounded, BarChart } from "@mui/icons-material";
import DefaultBadge from "../simple/DefaultBadge";
import DefaultButton from "../simple/DefaultButton";

import CircularProgress from '@mui/material/CircularProgress';

import PageTitle from "../PageTitle/PageTitle";

export default function ScenarioDetailsInfoPanel({
  scenarioData,
  difficulty_level,
  setSimulationData,
  setLoading,
  setSimulationId,
  setSimulationCallURL,
  setSnackbarMessage,
  isMobile,
}) {
  const theme = useTheme();
  const { brand, user, trainingProgram, authToken, refreshSimulations } =
    React.useContext(BrandContext);
  const scenario_id = scenarioData.id;
  const [value, setValue] = React.useState(0);
  const [backgroundImage, setBackgroundImage] = React.useState(null);
  const [simStarted, setSimStarted] = useState(false);

  useEffect(() => {
    if (scenarioData?.image) {
      setBackgroundImage(scenarioData.image);
      return;
    }
    if (trainingProgram?.image) {
      setBackgroundImage(trainingProgram.image);
    }
  }, [scenarioData, trainingProgram]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };
  // async to allow refreshSimulations to be awaited
  async function startScenario() {
    setLoading(true);
    setSimStarted(true);

    fetch(`${brand.api_base_url}/start_simulation`, {
      headers: {
        Authorization: `Token ${authToken}`,
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify({ scenario_id: scenario_id }),
    })
      .then((response) => response.json())
      .then(async (data) => {
        if (data["id"]) {
          setSimulationCallURL(data["webrtc_url"]);
          setSimulationId(data["id"]);
          setSimulationData(data);
          await refreshSimulations();
          setLoading(false);
        } else {
          console.log("Cannot start simulation:", data);
          setSnackbarMessage("Starting simulation failed");
          setLoading(false);
        }
      })
      .catch((err) => {
        setSnackbarMessage("Starting simulation failed");
        console.log(err.message);
        setLoading(false);
        setSimStarted(false);
      });
  }

  return (
    <Box
      sx={{
        width: "100%",
        "& #scenario-actions": {
          "& > button": {
            margin: 0,
          },
          "@media (max-width:1080px)": {
            flexDirection: "column-reverse",
            alignItems: "baseline",
            marginTop: "20px",
          },
        },
      }}
    >
      {scenarioData.briefing_video ? (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              backgroundSize: "cover",
              backgroundPosition: "center",
              maxHeight: "458px",
              width: "100%",
              borderRadius: theme.shape.radius.md,
              justifyContent: "flex-end",
              mr: theme.customSpacing["xl"],
              ...(backgroundImage && {
                backgroundColor: "white",
                backgroundImage: `url(${backgroundImage})`,
              }),
              marginBottom: "20px",
            }}
          >
            <ReactPlayer
              src={scenarioData.briefing_video} // "https://dev.gurunow.ai/media/Vague_Commitments__Lack_of_Follow_Through.mp4" -- Use this in local if video doesn't load
              controls={true}
              width="100%"
              height="auto"
            />
          </Box>
          {!isMobile && (
            <Box>
              <Typography
                sx={{
                  fontSize: theme.typography.displayXS.fontSize,
                  fontWeight: theme.typography.fontWeightMedium,
                  lineHeight: theme.typography.displayXS.lineHeight,
                  mt: theme.customSpacing["2xl"],
                  mb: theme.customSpacing.xs,
                }}
              >
                {scenarioData.name}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: theme.customSpacing.xl,
                }}
              >
                {scenarioData.author === "Unknown author" ? null : (
                  <Typography
                    sx={{
                      fontSize: theme.typography.textXS.fontSize,
                      fontWeight: theme.typography.fontWeightLight,
                    }}
                  >
                    A Lesson By {scenarioData.author}
                  </Typography>
                )}
                <DefaultBadge
                  variant="pillTransparent"
                  size="small"
                  // borderColor={theme.palette.foreground.badge_icon}
                  borderColor={
                    backgroundImage ? theme.palette.border.primary : null
                  }
                  startIcon={
                    <BarChart
                      sx={{
                        fontSize: theme.typography.textXS.fontSize,
                        color: theme.palette.foreground.badge_icon,
                      }}
                    />
                  }
                >
                  {difficulty_level
                    ? difficulty_level.charAt(0).toUpperCase() +
                      difficulty_level.slice(1)
                    : "Beginner"}
                </DefaultBadge>
              </Box>
            </Box>
          )}
        </>
      ) : (
        <Box
          id="scenario-header"
          sx={{
            "& h1": {
              marginBottom: "10px",
            },
            display: "flex",
            flexDirection: "column",
            backgroundSize: "cover",
            backgroundPosition: "center",
            maxHeight: "382px",
            width: "100%",
            borderRadius: theme.shape.radius.md,
            justifyContent: "flex-end",
            ...(backgroundImage && {
              backgroundColor: "white",
              backgroundImage: `url(${backgroundImage})`,
            }),
            marginBottom: "15px",
          }}
        >
          <Box
            className="scenario-bg"
            sx={
              backgroundImage && {
                position: "relative",
                width: "100%",
                zIndex: 1,
                background:
                  "linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(0,0,0,0.65) 100%)",
                borderRadius: theme.shape.radius.md,
                padding: theme.containers.paddingDesktop,
              }
            }
          >
            <Box
              sx={
                backgroundImage && {
                  height: "150px",
                }
              }
            ></Box>
            {!isMobile && (
              <>
                <PageTitle>{scenarioData.name}</PageTitle>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: theme.customSpacing.xl,
                  }}
                >
                  {scenarioData.author === "Unknown author" ? null : (
                    <Typography
                      sx={{
                        fontSize: theme.typography.textXS.fontSize,
                        fontWeight: theme.typography.fontWeightLight,
                      }}
                    >
                      A Lesson By {scenarioData.author}
                    </Typography>
                  )}
                  <DefaultBadge
                    variant="pillTransparent"
                    size="small"
                    // borderColor={theme.palette.foreground.badge_icon}
                    borderColor={
                      backgroundImage ? theme.palette.border.primary : null
                    }
                    startIcon={
                      <BarChart
                        sx={{
                          fontSize: theme.typography.textXS.fontSize,
                          color: theme.palette.foreground.badge_icon,
                        }}
                      />
                    }
                  >
                    {difficulty_level
                      ? difficulty_level.charAt(0).toUpperCase() +
                        difficulty_level.slice(1)
                      : "Beginner"}
                  </DefaultBadge>
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}
      {isMobile && (
        <Box>
          <PageTitle>{scenarioData.name}</PageTitle>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: theme.customSpacing.xl,
            }}
          >
            {scenarioData.author === "Unknown author" ? null : (
              <Typography
                sx={{
                  fontSize: theme.typography.textXS.fontSize,
                  fontWeight: theme.typography.fontWeightLight,
                }}
              >
                A Lesson By {scenarioData.author}
              </Typography>
            )}
            <DefaultBadge
              variant="pillTransparent"
              size="small"
              // borderColor={theme.palette.foreground.badge_icon}
              borderColor={
                backgroundImage ? theme.palette.border.primary : null
              }
              startIcon={
                <BarChart
                  sx={{
                    fontSize: theme.typography.textXS.fontSize,
                    color: theme.palette.foreground.badge_icon,
                  }}
                />
              }
            >
              {difficulty_level
                ? difficulty_level.charAt(0).toUpperCase() +
                  difficulty_level.slice(1)
                : "Beginner"}
            </DefaultBadge>
          </Box>
        </Box>
      )}

      <Box
        id="scenario-actions"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          textColor={theme.palette.text.button_hover}
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            mb: theme.customSpacing["2xl"],
            "& .MuiTab-root": {
              mr: theme.customSpacing["xl"],
              minWidth: 0,
              color: theme.palette.text.primary,
              fontWeight: theme.typography.fontWeightRegular,
              textTransform: "none",
              textDecoration: "none",
              alignItems: "flex-start",
              padding: 0,
              pr: theme.customSpacing.sm,
            },
            "& .Mui-disabled": {
              color: theme.palette.text.disabled,
              opacity: 1,
            },
            "& .Mui-selected": {
              color: theme.palette.text.button_hover,
              fontWeight: theme.typography.fontWeightBold,
            },
            "& .MuiTabs-indicator": {
              backgroundColor: theme.palette.background.button_primary_hover,
            },
          }}
        >
          <Tab label="Overview" />
          <Tab disabled label="Key Points" />
          {isMobile && scenarioData.resources === null && (
            <Tab disabled label="Resources" />
          )}
        </Tabs>
        <DefaultButton
          wideOnMobile
          onClick={startScenario}
          variant="contained"
          size="sm"
          sx={{ m: 2 }}
          disabled={simStarted}
          endIcon={!simStarted ? <ArrowForwardRounded /> : <CircularProgress size="18px" />}
        >
          {simStarted ? (
            <>Simulation Starting...</>
          ) : (
            <>
              {scenarioData.resources === null
                ? "Start Simulation"
                : "Start Skills Development"}
            </>
          )}
        </DefaultButton>
      </Box>
      {scenarioData.long_description && (
        <Typography
          sx={{
            fontSize: theme.typography.textMD.fontSize,
            color: theme.palette.text.gray,
            fontWeight: theme.typography.fontWeightLight,
            whiteSpace: "pre-wrap",
          }}
        >
          {scenarioData.long_description}
        </Typography>
      )}
    </Box>
  );
}
